import { kindInPlainWords } from '../failures/notFound';
import { formatCurrency, type Currency } from '../money';

/**
 * 🔒 The mail a routed document sends its approvers, and the reminder while it
 * waits (piece 22; T722; decision 2540). Pure, so the wording and the interval
 * rule are proved here and the API only addresses and records the send.
 */

/** 🔒 The days between reminders while a document waits (piece 21 names it): absent, zero or not a number reads off. */
export const APPROVAL_REMINDER_INTERVAL_KEY = 'approvals.reminderIntervalDays';

/** The page the mail links: the Pending Approvals queue, which is where an approver decides. */
export const PENDING_APPROVALS_LINK_PATH = '/approvals';

export const APPROVAL_MAIL_SUBJECT = 'A Document Is Waiting for Your Approval';
export const APPROVAL_REMINDER_SUBJECT = `Reminder: ${APPROVAL_MAIL_SUBJECT}`;

const DAY_MS = 24 * 60 * 60 * 1000;

/** The stored interval as whole days, or null where reminders are off. */
export function approvalReminderIntervalDays(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  const days = Math.floor(value);
  return days > 0 ? days : null;
}

/** 🔒 A reminder is due once a whole interval has passed since the last mail, or the routing where none went since. */
export function approvalReminderDue(input: { intervalDays: number | null; lastAt: Date; now: Date }): boolean {
  if (input.intervalDays === null) return false;
  return input.now.getTime() - input.lastAt.getTime() >= input.intervalDays * DAY_MS;
}

export interface ApprovalMailFacts {
  /** The adapter's document type, such as `Quote` or `PurchaseOrder`. */
  documentType: string;
  number: string | null;
  submitterName: string;
  /** Null on a request that carries no amount, such as a merge (decision 1484). */
  total: Currency | null;
  /** The queue's address, absolute. */
  link: string;
  reminder: boolean;
  /**
   * 🔒 Set on the mail a step approval sends to the next step's people (T2005; decision 2540): the earlier approver's
   * name, or an empty string where it is unknown. Absent on a routing's mail and on a reminder.
   */
  approvedEarlierBy?: string;
}

/** A kind as a heading reads it: Purchase Order, Sales Order, Quote. */
function kindTitle(documentType: string): string {
  return kindInPlainWords(documentType)
    .split(' ')
    .map((word) => (word.length === 0 ? word : `${word.charAt(0).toUpperCase()}${word.slice(1)}`))
    .join(' ');
}

/** 🔒 The subject and body one approver receives: the number, the kind, the submitter, the total and the queue's link. */
export function approvalWaitingMail(facts: ApprovalMailFacts): { subject: string; body: string } {
  const kind = kindTitle(facts.documentType);
  const named = facts.number ? `${kind} ${facts.number}` : `A ${kindInPlainWords(facts.documentType)}`;
  const opening = facts.reminder ? `${named} is still waiting for your approval.` : `${named} is waiting for your approval.`;
  const earlier = facts.approvedEarlierBy === undefined ? [] : [facts.approvedEarlierBy ? `An earlier approval step was approved by ${facts.approvedEarlierBy}.` : 'An earlier approval step was approved.', ''];
  const lines = [
    opening,
    '',
    ...earlier,
    `Kind: ${kind}`,
    ...(facts.number ? [`Number: ${facts.number}`] : []),
    `Submitted by: ${facts.submitterName || 'Unknown'}`,
    ...(facts.total ? [`Total: ${formatCurrency(facts.total)}`] : []),
    '',
    `Open Pending Approvals: ${facts.link}`,
  ];
  return { subject: facts.reminder ? APPROVAL_REMINDER_SUBJECT : APPROVAL_MAIL_SUBJECT, body: lines.join('\n') };
}

/** Who one approval mail reached, by name, for the document's History. */
export interface ApprovalMailOutcome {
  mailed: readonly string[];
  /** People the step routes to with no open work email on their employment. */
  noAddress: readonly string[];
  /** People whose send the transport refused; each refusal is in the failure queue. */
  failed: readonly string[];
}

/** 🔒 The History sentence beneath an approval mail's row: who was mailed, and who was not and why. */
export function approvalMailHistory(outcome: ApprovalMailOutcome): string {
  const parts: string[] = [];
  if (outcome.mailed.length > 0) parts.push(`Mailed ${outcome.mailed.join(', ')}.`);
  if (outcome.noAddress.length > 0) parts.push(`Not mailed, no work email on file: ${outcome.noAddress.join(', ')}.`);
  if (outcome.failed.length > 0) parts.push(`The mail to ${outcome.failed.join(', ')} failed and waits in the failure queue.`);
  return parts.length > 0 ? parts.join(' ') : 'Nobody was mailed: the step routes to no active person.';
}
